"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import smoothscroll from "smoothscroll-polyfill";
import classNames from "classnames/bind";
import { Size as SizeType } from "./types";
import { SizeItems } from "./components/SizeItems";
import { scrollIntoViewAndWait } from "@/utils/scroll/scroll";
import { SizeLabel } from "./components/SizeLabel";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { useSelectorListColumns } from "./useSelectorListColumns";
import { getSizeOfferLabel } from "./utils/getSizeOfferLabel";
import {
  ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
  WISHLIST_NO_VARIANT_CODE,
} from "@/constants/constants";
import styles from "./Sizes.module.scss";
import { Alert } from "@JohnLewisPartnership/jl-design-system/dist/components/alert/Alert";

const cx = classNames.bind(styles);

type SizeError = {
  message: string;
  sizes: SizeType[];
};

type Props = {
  sizes: SizeType[];
  isGroupingAllowed?: boolean;
  showMore?: number;
  replaceHistoryEntryOnVariantChange: boolean;
  FitToolAndSizeChartComponent: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  withFitToolAndSizeChart: boolean;
};

export function SizesUI({
  sizes,
  isGroupingAllowed = false,
  showMore,
  replaceHistoryEntryOnVariantChange,
  FitToolAndSizeChartComponent,
  withFitToolAndSizeChart,
}: Props) {
  const [sizeError, setSizeError] = useState<SizeError | null>(null);
  const errorMessageRef = useRef<HTMLDivElement | null>(null);
  const currentSizesRef = useRef(sizes);
  const columnItems = useMemo(
    () =>
      sizes.map((size) => ({
        name: size.name,
        offerLabel:
          (size.isOutOfStock ?? size.unavailable)
            ? undefined
            : getSizeOfferLabel(size),
      })),
    [sizes],
  );
  const { containerRef, desktopColumns, mobileColumns, measurementRef } =
    useSelectorListColumns(columnItems);
  const selectedSize = sizes.find((size) => size.isSelected);
  const selectedSizeValueFromProps = selectedSize?.id;
  const selectedSizeValueRef = useRef(selectedSizeValueFromProps);
  const [selectedSizeValue, setSelectedSizeValue] = useState(
    selectedSizeValueFromProps,
  );
  const groupedSizes = useMemo(() => {
    const groupedSizes: Record<string, SizeType[]> = {};

    sizes.forEach((size) => {
      groupedSizes[size.sizeHeadline] = [
        ...(groupedSizes[size.sizeHeadline] || []),
        size,
      ];
    });

    return groupedSizes;
  }, [sizes]);

  useEffect(() => {
    currentSizesRef.current = sizes;
  }, [sizes]);

  useEffect(() => {
    if (selectedSizeValueRef.current === selectedSizeValueFromProps) {
      return;
    }

    selectedSizeValueRef.current = selectedSizeValueFromProps;
    setSelectedSizeValue(selectedSizeValueFromProps);
  }, [selectedSizeValueFromProps]);

  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setSizeError({
        message: "Select a size",
        sizes: currentSizesRef.current,
      });
      scrollToError();
    };

    const handleWishlistNoSizeSelectedError = () => {
      setSizeError({
        message: "Please choose a size first",
        sizes: currentSizesRef.current,
      });
      scrollToError();
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const { errors = [] } = event.detail;
      if (errors.some(({ code }) => code === NO_VARIANT_CODE)) {
        handleNoSizeSelectedError();
      }
      if (errors.some(({ code }) => code === WISHLIST_NO_VARIANT_CODE)) {
        handleWishlistNoSizeSelectedError();
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );
    window.addEventListener(
      ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
      handleWishlistNoSizeSelectedError,
    );
    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );
      window.removeEventListener(
        ADD_TO_WISHLIST_ESI_WARNING_OR_ERROR,
        handleWishlistNoSizeSelectedError,
      );
      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  if (!sizes.length) {
    return null;
  }

  const scrollToError = () => {
    setTimeout(() => {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;

      if (sizeErrorElement) {
        scrollIntoViewAndWait(sizeErrorElement, {
          behavior: "smooth",
        }).then(() => {
          errorMessageRef.current?.focus({ preventScroll: true });
        });
      }
    }, 0);
  };

  const isGrouped = isGroupingAllowed && Object.keys(groupedSizes).length > 1;
  const activeSizeError = sizeError?.sizes === sizes ? sizeError : null;
  const onSizeSelected = (value: string) => {
    selectedSizeValueRef.current = value;
    setSelectedSizeValue(value);
    setSizeError(null);
    dispatchMonetateQEvent("product-pages-size-selector-click");
  };

  return (
    <div data-component="Sizes">
      <h2 className={styles.visuallyHidden}>Sizes</h2>
      {activeSizeError && (
        <div
          id="size:error"
          data-testid="size:error"
          ref={errorMessageRef}
          tabIndex={-1}
        >
          <Alert
            className={styles.message}
            body={activeSizeError.message}
            type="error"
          />
        </div>
      )}
      <SizeLabel
        selectedSizeLabel={selectedSize?.name}
        sublabel={selectedSize?.sublabel}
      />
      {withFitToolAndSizeChart && (
        <FitToolAndSizeChartComponent className={styles.fitToolAndSizeChart} />
      )}
      <div ref={containerRef} className={styles.sizeContainer}>
        <div
          aria-hidden="true"
          className={styles.measurementContainer}
          ref={measurementRef}
        >
          {columnItems.map(({ name, offerLabel }, index) => (
            <span
              className={styles.measurementLabel}
              data-label={name}
              data-offer-label={offerLabel}
              key={`size-measurement-${index}`}
            />
          ))}
        </div>
        <ul className={cx("groupList")}>
          {isGrouped ? (
            Object.entries(groupedSizes).map(([groupName, sizes]) => (
              <SizeItems
                desktopColumns={desktopColumns}
                mobileColumns={mobileColumns}
                key={groupName}
                sizes={sizes}
                showMore={showMore}
                groupName={groupName}
                selectedSizeValue={selectedSizeValue}
                onSizeSelected={onSizeSelected}
                replaceHistoryEntryOnVariantChange={
                  replaceHistoryEntryOnVariantChange
                }
              />
            ))
          ) : (
            <SizeItems
              desktopColumns={desktopColumns}
              mobileColumns={mobileColumns}
              sizes={sizes}
              showMore={showMore}
              selectedSizeValue={selectedSizeValue}
              onSizeSelected={onSizeSelected}
              replaceHistoryEntryOnVariantChange={
                replaceHistoryEntryOnVariantChange
              }
            />
          )}
        </ul>
      </div>
    </div>
  );
}
